import { Fragment, useEffect, useId, useMemo, useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import { ChevronRightIcon, FileTextIcon, FolderIcon } from 'lucide-react';

import { ApiError } from '@mds/shared/lib/api';
import { cn } from '@mds/shared/lib/utils';

import { controlCls, FieldError } from '@/components/ui/field';
import useDebounced from '@mds/shared/hooks/use-debounced';
import * as files from '@/files/files.api';
import { ROOT_LABEL, buildTree, pathTo } from '@/files/folders';

/** A picture already attached to the record, which the library page may not include. */
export interface KnownPicture {
  fileId: string;
  contentUrl: string;
  originalFilename: string;
}

/** What the picker picks: pictures for galleries, or a PDF for a downloadable document. */
export type PickerKind = 'image' | 'pdf';

const NOUNS: Record<PickerKind, { one: string; many: string }> = {
  image: { one: 'picture', many: 'pictures' },
  pdf: { one: 'PDF', many: 'PDFs' },
};

/** Enough of a stored file to draw a thumbnail for it. */
interface Thumb {
  url: string;
  name: string;
}

const crumbButtonCls =
  'rounded-md font-semibold text-brand-text underline-offset-4 hover:underline focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)';

const count = (n: number, noun: string) => `${n} ${noun}${n === 1 ? '' : 's'}`;

/** One page of the library is plenty to pick from; search narrows it. */
const PAGE_SIZE = 24;

const seedOf = (known: readonly KnownPicture[]): Record<string, Thumb> =>
  Object.fromEntries(
    known.map((item) => [item.fileId, { url: item.contentUrl, name: item.originalFilename }]),
  );

/**
 * A gallery picked out of the file library — an event's, or a space's — or,
 * with `kind="pdf"`, a document such as the booking policy's download.
 *
 * Order is the order they were picked and the first one leads, so removing a
 * chosen picture and picking it again sends it to the end — which is also how
 * you change which one leads. The value is stored-file IDs, which is what the
 * API takes; the thumbnails are resolved from whatever the library has returned
 * so far, seeded with the record's own gallery so pictures that are not on the
 * first page still draw.
 *
 * The library is browsed the way the Files page shows it: one folder at a time,
 * starting at the top level, with its subfolders as tiles ahead of its
 * pictures and a path back up. A search looks through every folder at once.
 */
export default function GalleryPicker({
  value,
  onChange,
  known = [],
  optional = false,
  label = 'Pictures',
  max,
  error,
  onBlur,
  kind = 'image',
}: {
  /** Stored-file IDs in display order. */
  value: string[];
  onChange: (next: string[]) => void;
  /** The record's current gallery, which the library page may not include. */
  known?: readonly KnownPicture[];
  /** An event needs a cover; a space can go without pictures. */
  optional?: boolean;
  label?: string;
  /**
   * The most that can be picked. At 1 the picker is a single choice, and
   * picking another picture replaces the current one.
   */
  max?: number;
  /** A problem with the pictures picked, from the form's validation. */
  error?: string;
  onBlur?: () => void;
  /** Pictures by default; `pdf` lists PDFs instead, drawn as named document tiles. */
  kind?: PickerKind;
}) {
  const noun = NOUNS[kind];
  const errorId = useId();
  const [search, setSearch] = useState('');
  const settled = useDebounced(search);
  /** Everything the library has shown so far, so a picture stays drawn after a search moves on. */
  const [seen, setSeen] = useState<Record<string, Thumb>>({});
  /** The folder being browsed; null is the top level. */
  const [folderId, setFolderId] = useState<string | null>(null);
  const searching = settled.trim() !== '';

  // The same key as the Files page, so the two share one copy of the tree.
  const folders = useQuery({ queryKey: ['files', 'folders'], queryFn: files.listFolders });
  const tree = useMemo(() => buildTree(folders.data ?? []), [folders.data]);
  const current = folderId ? (tree.byId.get(folderId) ?? null) : null;
  const subfolders = searching ? [] : (tree.children.get(current?.id ?? null) ?? []);
  const crumbs = [{ id: null as string | null, name: ROOT_LABEL }, ...pathTo(tree, folderId)];

  // A folder deleted while it was open falls back to the top level, not an error.
  useEffect(() => {
    if (folderId && folders.isSuccess && !tree.byId.has(folderId)) setFolderId(null);
  }, [folderId, folders.isSuccess, tree]);

  const library = useQuery({
    queryKey: ['files', 'gallery', { kind, folderId, search: settled }],
    queryFn: () =>
      files.listFiles({
        q: settled || undefined,
        // A search looks through every folder; browsing shows one.
        folderId: searching ? undefined : (folderId ?? 'root'),
        // One MIME type per request: a PDF list asks for it, images are sieved below.
        mimeType: kind === 'pdf' ? 'application/pdf' : undefined,
        visibility: 'public',
        uploadStatus: 'ready',
        limit: PAGE_SIZE,
        sort: 'createdAt',
        direction: 'desc',
      }),
  });

  // Only public, ready images can go in a gallery, and the list endpoint filters
  // by one MIME type at a time — so the three image types are sieved here.
  const pool = useMemo(
    () =>
      (library.data?.data ?? []).filter(
        (file) =>
          file.deletedAt === null &&
          file.contentUrl !== null &&
          (kind === 'pdf'
            ? file.mimeType === 'application/pdf'
            : file.mimeType.startsWith('image/')),
      ),
    [library.data, kind],
  );

  useEffect(() => {
    if (pool.length === 0) return;
    setSeen((held) => ({
      ...held,
      ...Object.fromEntries(
        pool.map((file) => [
          file.id,
          { url: file.contentUrl as string, name: file.originalFilename },
        ]),
      ),
    }));
  }, [pool]);

  // `known` is rebuilt by the caller on every render, so this merges rather
  // than being held in state — an effect on it would never stop firing.
  const thumbs = useMemo(() => ({ ...seedOf(known), ...seen }), [known, seen]);

  const full = max !== undefined && max > 1 && value.length >= max;
  const toggle = (fileId: string) => {
    if (value.includes(fileId)) return onChange(value.filter((id) => id !== fileId));
    if (max === 1) return onChange([fileId]);
    if (full) return;
    onChange([...value, fileId]);
  };

  return (
    <fieldset className="m-0 border-0 p-0" onBlur={onBlur}>
      <legend
        className={cn(
          'font-mono text-sm font-semibold tracking-wider uppercase',
          error ? 'text-danger-text' : 'text-ink-secondary',
        )}
      >
        {label}
      </legend>
      <p className="m-0 mt-2 mb-3 text-base text-ink-muted">
        {max === 1
          ? value.length === 0
            ? 'Optional. Pick one from the library.'
            : 'Pick another to replace it, or click it to remove it.'
          : value.length === 0
            ? optional
              ? 'Optional. The first one you pick leads.'
              : 'Pick at least one. The first one becomes the cover.'
            : full
              ? `${value.length} of ${max} picked. Remove one to add another.`
              : `${value.length} picked · the first is the cover.`}
      </p>

      {value.length > 0 && (
        <ul className="m-0 mb-4 flex list-none gap-2 overflow-x-auto p-0">
          {value.map((fileId, at) => {
            const thumb = thumbs[fileId];
            return (
              <li key={fileId} className="shrink-0">
                <button
                  type="button"
                  onClick={() => toggle(fileId)}
                  aria-label={`Remove ${noun.one} ${at + 1}${thumb ? `, ${thumb.name}` : ''}`}
                  className="relative block overflow-hidden rounded-xl border border-brand-edge focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)"
                >
                  {kind === 'pdf' ? (
                    <DocumentTile name={thumb?.name ?? `PDF ${at + 1}`} className="w-56 pt-6" />
                  ) : thumb ? (
                    <img src={thumb.url} alt="" className="block h-20 w-28 object-cover" />
                  ) : (
                    <span className="flex h-20 w-28 items-center justify-center bg-raised px-2 text-center font-mono text-xs text-ink-muted">
                      Picture {at + 1}
                    </span>
                  )}
                  <span className="absolute top-1.5 left-1.5 rounded-lg bg-panel px-2 py-0.5 font-mono text-xs font-semibold tracking-label text-on-panel uppercase">
                    {max === 1 ? 'Picked' : at === 0 ? 'Cover' : at + 1}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <input
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search every folder"
        aria-label={`Search ${noun.many}`}
        // The search box is the one control here that takes focus, so it carries
        // the error: a failed save moves focus to it and it announces why.
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? errorId : undefined}
        className={controlCls + ' w-full'}
      />
      {error && (
        <div className="mt-2">
          <FieldError id={errorId}>{error}</FieldError>
        </div>
      )}

      <nav aria-label="Folder path" className="mt-3 flex flex-wrap items-center gap-1.5 text-base">
        {searching ? (
          <span className="text-ink-muted">
            Searching every folder.{' '}
            <button type="button" onClick={() => setSearch('')} className={crumbButtonCls}>
              Back to {current?.name ?? ROOT_LABEL}
            </button>
          </span>
        ) : (
          crumbs.map((crumb, at) => (
            <Fragment key={crumb.id ?? 'root'}>
              {at > 0 && <ChevronRightIcon className="size-4 text-ink-muted" aria-hidden="true" />}
              {at === crumbs.length - 1 ? (
                <span aria-current="location" className="font-semibold">
                  {crumb.name}
                </span>
              ) : (
                <button
                  type="button"
                  onClick={() => setFolderId(crumb.id)}
                  className={crumbButtonCls}
                >
                  {crumb.name}
                </button>
              )}
            </Fragment>
          ))
        )}
      </nav>

      {library.isPending ? (
        <p className="m-0 mt-3 text-base text-ink-muted">Loading {noun.many}…</p>
      ) : library.isError ? (
        <p role="alert" className="m-0 mt-3 text-base text-danger-text">
          {library.error instanceof ApiError
            ? library.error.message
            : 'The file library could not be reached.'}
        </p>
      ) : pool.length === 0 && subfolders.length === 0 ? (
        <p className="m-0 mt-3 text-base text-ink-muted">
          {searching
            ? `No ${noun.many} in any folder match that.`
            : current
              ? `No public, ready ${noun.many} in this folder.`
              : `No public ${noun.many} are ready yet. Upload some from Files first.`}
        </p>
      ) : (
        <ul className="m-0 mt-3 grid list-none grid-cols-2 gap-2 p-0 sm:grid-cols-4">
          {subfolders.map((folder) => {
            const inside = tree.children.get(folder.id)?.length ?? 0;
            return (
              <li key={folder.id}>
                <button
                  type="button"
                  onClick={() => setFolderId(folder.id)}
                  aria-label={`Open folder ${folder.name}`}
                  title={folder.name}
                  className="flex h-20 w-full items-center gap-3 overflow-hidden rounded-xl border border-hairline bg-surface px-3 text-left transition-colors hover:border-brand-edge focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)"
                >
                  <span className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-brand-edge/35 bg-brand-wash text-brand-strong">
                    <FolderIcon className="size-5" strokeWidth={2} aria-hidden="true" />
                  </span>
                  <span className="flex min-w-0 flex-col">
                    <span className="truncate font-semibold">{folder.name}</span>
                    <span className="truncate text-sm text-ink-muted">
                      {count(folder.fileCount, 'file')}
                      {inside > 0 && ` · ${count(inside, 'folder')}`}
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
          {pool.map((file) => {
            const at = value.indexOf(file.id);
            const picked = at >= 0;
            return (
              <li key={file.id}>
                <button
                  type="button"
                  onClick={() => toggle(file.id)}
                  aria-pressed={picked}
                  aria-label={
                    picked ? `Remove ${file.originalFilename}` : `Add ${file.originalFilename}`
                  }
                  title={file.originalFilename}
                  className={cn(
                    'relative block w-full overflow-hidden rounded-xl border transition-colors focus-visible:outline-(length:--focus-ring-width) focus-visible:outline-offset-(--focus-ring-offset) focus-visible:outline-(color:--focus-ring-color)',
                    picked ? 'border-brand-edge' : 'border-hairline hover:border-brand-edge',
                  )}
                >
                  {kind === 'pdf' ? (
                    <DocumentTile name={file.originalFilename} className={picked ? 'pt-6' : ''} />
                  ) : (
                    <img
                      // The small preview where there is one: a page of twenty-four
                      // full-size photos is a lot to pull for a picker.
                      src={file.thumbnailUrl ?? (file.contentUrl as string)}
                      alt=""
                      className="block h-20 w-full object-cover"
                    />
                  )}
                  {picked && (
                    <span className="absolute top-1.5 left-1.5 rounded-lg bg-panel px-2 py-0.5 font-mono text-xs font-semibold tracking-label text-on-panel uppercase">
                      {max === 1 ? 'Picked' : at === 0 ? 'Cover' : at + 1}
                    </span>
                  )}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </fieldset>
  );
}

/** A PDF as a tile the height of a picture: its icon and its name. */
function DocumentTile({ name, className }: { name: string; className?: string }) {
  return (
    <span
      className={cn('flex h-20 w-full items-center gap-3 bg-surface px-3 text-left', className)}
    >
      <span className="flex size-10 shrink-0 items-center justify-center rounded-lg border border-hairline bg-raised text-ink-muted">
        <FileTextIcon className="size-5" strokeWidth={2} aria-hidden="true" />
      </span>
      <span className="min-w-0 truncate font-semibold">{name}</span>
    </span>
  );
}
